import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { Plus, MapPin, PencilSimple, Trash, X, Paperclip, UploadSimple, DownloadSimple, Eye, FilePdf, FileXls, FileImage, FileVideo, File as FileIcon, Archive, ArrowCounterClockwise, Star, Buildings, HardHat, FileText, MagnifyingGlass, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import FilePreviewModal from "@/components/FilePreviewModal";
import MediaGalleryModal from "@/components/MediaGalleryModal";
import EmptyState from "@/components/EmptyState";
import ModalPortal from "@/components/ModalPortal";
import { usePersonalliggare } from "@/hooks/usePersonalliggare";
import PersonalliggareModal from "@/components/PersonalliggareModal";
import { useAta } from "@/hooks/useAta";
import AtaModal from "@/components/AtaModal";

const empty = {
  name: "", address: "", description: "", client: "",
  client_org_number: "", client_contact_name: "", client_email: "", client_phone: "",
  contract_reference: "",
  client_type: "business",     // "business" | "private"
  deduction_type: "none",      // "none" | "rut" | "rot"
  client_ssn: "",              // input-fält (okrypterat); backend krypterar och tömmer i respons
  property_designation: "",    // ROT-krav: fastighetsbeteckning
  hourly_rate_sek: "",
  status: "active", task_ids: [], favorite_task_ids: [],
};

export default function Objects() {
  const [objects, setObjects] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [fileCounts, setFileCounts] = useState({});
  const [fileFilter, setFileFilter] = useState("all"); // all | pdf | xls | dxf
  const [viewFilter, setViewFilter] = useState("active"); // active | archived | all
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [filesObject, setFilesObject] = useState(null); // object currently shown in files modal
  const [pllObject, setPllObject] = useState(null); // object currently shown in personalliggare modal
  const { enabled: pllEnabled, id06_site_number: pllSiteNumber } = usePersonalliggare();
  const [ataObject, setAtaObject] = useState(null); // object currently shown in ÄTA modal
  const { enabled: ataEnabled } = useAta();
  const [deviationsObject, setDeviationsObject] = useState(null); // avvikelselistan-modal
  const [exportingId, setExportingId] = useState(null);
  const [me, setMe] = useState(null);
  const [orgnrLoading, setOrgnrLoading] = useState(false);
  const isAdmin = me?.role === "admin";

  const handleOrgnrLookup = async (orgnr) => {
    if (!orgnr || orgnrLoading) return;
    setOrgnrLoading(true);
    try {
      const r = await api.get(`/lookup/company/${encodeURIComponent(orgnr.trim())}`);
      setForm((prev) => ({
        ...prev,
        client: prev.client || r.data.name || "",
        client_org_number: r.data.organisation_number || prev.client_org_number,
        address: prev.address || r.data.address || "",
      }));
      toast.success(`Hämtade uppgifter för ${r.data.name || "företaget"}${r.data._cached ? " (cache)" : ""}`);
    } catch (err) {
      const status = err.response?.status;
      if (status === 503) {
        toast.error("Bolagsverket-autofill är inte aktiverat. Se Inställningar.");
      } else if (status === 404) {
        toast.error("Företaget hittades inte hos Bolagsverket");
      } else if (status === 422) {
        toast.error(err.response?.data?.detail || "Ogiltigt organisationsnummer");
      } else {
        toast.error(err.response?.data?.detail || "Kunde inte hämta uppgifter");
      }
    } finally {
      setOrgnrLoading(false);
    }
  };

  const load = async () => {
    try {
      setLoading(true);
      const [r, meRes, countsRes, tasksRes] = await Promise.all([
        api.get("/objects"),
        api.get("/auth/me"),
        api.get("/objects/file-counts"),
        api.get("/tasks"),
      ]);
      setObjects(r.data);
      setMe(meRes.data);
      setFileCounts(countsRes.data || {});
      setAllTasks((tasksRes.data || []).filter((t) => t.active));
    } catch (e) {
      toast.error("Kunde inte hämta objekt");
    } finally {
      setLoading(false);
    }
  };

  const reloadCounts = async () => {
    try {
      const r = await api.get("/objects/file-counts");
      setFileCounts(r.data || {});
    } catch (e) {
      /* non-blocking */
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(empty);
    setOpen(true);
  };

  const openEdit = (o) => {
    setEditing(o);
    setForm({
      name: o.name || "",
      address: o.address || "",
      description: o.description || "",
      client: o.client || "",
      client_org_number: o.client_org_number || "",
      client_contact_name: o.client_contact_name || "",
      client_email: o.client_email || "",
      client_phone: o.client_phone || "",
      contract_reference: o.contract_reference || "",
      client_type: o.client_type || "business",
      deduction_type: o.deduction_type || "none",
      // Kundens personnummer visas aldrig i klartext från backend — bara masked.
      // Lämna fältet tomt; om användaren vill uppdatera fyller de i på nytt.
      client_ssn: "",
      property_designation: o.property_designation || "",
      hourly_rate_sek: o.hourly_rate_sek != null ? String(o.hourly_rate_sek) : "",
      status: o.status || "active",
      task_ids: Array.isArray(o.task_ids) ? [...o.task_ids] : [],
      favorite_task_ids: Array.isArray(o.favorite_task_ids) ? [...o.favorite_task_ids] : [],
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Namn krävs");
      return;
    }
    if (!form.task_ids || form.task_ids.length === 0) {
      toast.error("Välj minst ett moment som ska ingå i objektet");
      return;
    }
    // Säkerställ att favoriter är en delmängd av valda moment
    const cleanFavs = (form.favorite_task_ids || []).filter((id) => form.task_ids.includes(id));
    const payload = { ...form, favorite_task_ids: cleanFavs };
    // Kunduppgifter: konvertera timpris till number, skicka bara ssn om användaren fyllt i det
    if (payload.hourly_rate_sek === "" || payload.hourly_rate_sek == null) {
      payload.hourly_rate_sek = null;
    } else {
      payload.hourly_rate_sek = parseFloat(payload.hourly_rate_sek) || null;
    }
    // Skicka bara ssn om ändrat (tom sträng = radera, undefined = orört)
    if (payload.client_ssn === "") delete payload.client_ssn;
    // Privatperson utan RUT/ROT = default "none"
    if (payload.client_type !== "private") payload.deduction_type = "none";
    try {
      if (editing) {
        await api.patch(`/objects/${editing.object_id}`, payload);
        toast.success("Objekt uppdaterat");
      } else {
        await api.post("/objects", payload);
        toast.success("Objekt skapat");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fel vid sparande");
    }
  };

  const toggleTask = (taskId) => {
    setForm((f) => {
      const has = f.task_ids.includes(taskId);
      if (has) {
        return {
          ...f,
          task_ids: f.task_ids.filter((id) => id !== taskId),
          favorite_task_ids: f.favorite_task_ids.filter((id) => id !== taskId),
        };
      }
      return { ...f, task_ids: [...f.task_ids, taskId] };
    });
  };

  const toggleFavorite = (taskId) => {
    setForm((f) => {
      // Måste ingå i task_ids först
      if (!f.task_ids.includes(taskId)) {
        return {
          ...f,
          task_ids: [...f.task_ids, taskId],
          favorite_task_ids: [...f.favorite_task_ids, taskId],
        };
      }
      const isFav = f.favorite_task_ids.includes(taskId);
      return {
        ...f,
        favorite_task_ids: isFav
          ? f.favorite_task_ids.filter((id) => id !== taskId)
          : [...f.favorite_task_ids, taskId],
      };
    });
  };

  const remove = async (id) => {
    if (!window.confirm("Ta bort objektet? Eventuella bifogade filer raderas också.")) return;
    try {
      await api.delete(`/objects/${id}`);
      toast.success("Borttaget");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fel");
    }
  };

  const downloadRutRot = async (o) => {
    const label = (o.deduction_type || "rot").toUpperCase();
    try {
      const resp = await fetch(`${API}/objects/${o.object_id}/rut-rot-underlag`, {
        method: "GET",
        credentials: "include",
      });
      if (!resp.ok) {
        let detail = `Kunde inte hämta ${label}-underlag`;
        try {
          const j = await resp.json();
          detail = j.detail || detail;
        } catch (_) { /* not json */ }
        toast.error(detail);
        return;
      }
      const blob = await resp.blob();
      const safe = (o.name || "objekt").replace(/[^\w\-]+/g, "_").replace(/^_+|_+$/g, "") || "objekt";
      const ts = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${label}-underlag-${safe}-${ts}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${label}-underlag nedladdat`);
    } catch (e) {
      toast.error(`Fel vid nedladdning: ${e.message || e}`);
    }
  };

  const exportArchive = async (o) => {
    const isReexport = o.status === "archived";
    const safeBase = (o.name || "objekt").replace(/[^\w\-]+/g, "_").replace(/^_+|_+$/g, "") || "objekt";
    const ts = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    const suggestedName = `${safeBase}-arkiv-${ts}.zip`;
    const supportsPicker = typeof window.showSaveFilePicker === "function";

    // 1) Ask for destination BEFORE archiving (so user can cancel safely)
    let fileHandle = null;
    if (supportsPicker) {
      try {
        fileHandle = await window.showSaveFilePicker({
          suggestedName,
          types: [{
            description: "ZIP-arkiv",
            accept: { "application/zip": [".zip"] },
          }],
        });
      } catch (err) {
        // User cancelled picker — abort silently, do NOT archive
        if (err && (err.name === "AbortError" || err.code === 20)) return;
        // Other error: fall back to default download
        fileHandle = null;
      }
    } else {
      const msg = isReexport
        ? `Webbläsaren stöder inte val av sparplats. Arkivet laddas ner till din standard-nedladdningsmapp för "${o.name}". Fortsätt?`
        : `Webbläsaren stöder inte val av sparplats. Arkivet laddas ner till din standard-nedladdningsmapp och objektet markeras som "Arkiverad". Fortsätt?`;
      if (!window.confirm(msg)) return;
    }

    try {
      setExportingId(o.object_id);
      const resp = await fetch(`${API}/objects/${o.object_id}/export`, {
        method: "POST",
        credentials: "include",
      });
      if (!resp.ok) {
        let detail = "Export misslyckades";
        try {
          const j = await resp.json();
          detail = j.detail || detail;
        } catch (_) { /* not json */ }
        throw new Error(detail);
      }
      const blob = await resp.blob();

      if (fileHandle) {
        // Write directly to user-chosen location
        const writable = await fileHandle.createWritable();
        await writable.write(blob);
        await writable.close();
        toast.success(
          isReexport ? "Arkiv sparat på vald plats" : "Objekt exporterat och arkivet sparat på vald plats"
        );
      } else {
        // Fallback: standard download to default folder
        const cd = resp.headers.get("Content-Disposition") || "";
        let filename = suggestedName;
        const m = cd.match(/filename\*=UTF-8''([^;]+)/i) || cd.match(/filename="?([^";]+)"?/i);
        if (m && m[1]) {
          try { filename = decodeURIComponent(m[1]); } catch (_) { filename = m[1]; }
        }
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        toast.success(isReexport ? "Arkiv nedladdat" : "Objekt exporterat och arkiverat");
      }
      load();
    } catch (e) {
      toast.error(e.message || "Export misslyckades");
    } finally {
      setExportingId(null);
    }
  };

  const restoreObject = async (o) => {
    if (!window.confirm(`Återställ "${o.name}" från arkivet? Objektet får status "Slutförd" igen och kan redigeras.`)) return;
    try {
      await api.post(`/objects/${o.object_id}/restore`);
      toast.success("Objekt återställt");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte återställa objekt");
    }
  };

  return (
    <div className="space-y-6" data-testid="objects-page">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-4xl md:text-5xl tracking-tight">Objekt</h1>
          <p className="text-text-muted mt-1">
            {(() => {
              const a = objects.filter((o) => o.status !== "archived").length;
              const ar = objects.filter((o) => o.status === "archived").length;
              return `${a} aktiva · ${ar} arkiverade`;
            })()}
          </p>
        </div>
        <button
          data-testid="new-object-button"
          onClick={openCreate}
          disabled={!isAdmin}
          className={`tk-btn-primary inline-flex items-center gap-2 ${!isAdmin ? "hidden" : ""}`}
        >
          <Plus size={18} weight="bold" /> Nytt objekt
        </button>
      </header>

      <ViewFilterTabs
        value={viewFilter}
        onChange={setViewFilter}
        counts={{
          active: objects.filter((o) => o.status !== "archived").length,
          archived: objects.filter((o) => o.status === "archived").length,
          all: objects.length,
        }}
      />

      <FileFilterChips
        value={fileFilter}
        onChange={setFileFilter}
        fileCounts={fileCounts}
      />

      <div className="tk-card p-2 md:p-4">
        {loading ? (
          <p className="text-center text-text-muted py-12 text-sm">Laddar…</p>
        ) : (() => {
          const byView = viewFilter === "active"
            ? objects.filter((o) => o.status !== "archived")
            : viewFilter === "archived"
              ? objects.filter((o) => o.status === "archived")
              : objects;
          const filtered = fileFilter === "all"
            ? byView
            : byView.filter((o) => (fileCounts[o.object_id]?.[fileFilter] || 0) > 0);
          if (objects.length === 0) {
            return (
              <EmptyState
                testId="objects-empty"
                icon={Buildings}
                title="Inga objekt ännu"
                description={
                  isAdmin
                    ? "Skapa ditt första objekt/projekt så kan teamet börja stämpla tid direkt. Du kan koppla moment, ladda upp ritningar och arkivera när det är klart."
                    : "Din admin har inte skapat några objekt ännu. Så snart det finns objekt kan du stämpla in tid på dem."
                }
                action={
                  isAdmin && (
                    <button
                      data-testid="empty-new-object-button"
                      onClick={openCreate}
                      className="tk-btn-primary inline-flex items-center gap-2"
                    >
                      <Plus size={18} weight="bold" /> Skapa första objektet
                    </button>
                  )
                }
              />
            );
          }
          if (filtered.length === 0) {
            const emptyMsg = viewFilter === "archived"
              ? "Inga arkiverade objekt ännu."
              : fileFilter === "all"
                ? "Inga aktiva objekt."
                : `Inga objekt har ${filterLabel(fileFilter)}-filer.`;
            return (
              <p
                data-testid="objects-empty-filter"
                className="text-center text-text-muted py-12 text-sm"
              >
                {emptyMsg}
              </p>
            );
          }
          return (
          <div>
            <div className="hidden md:grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-wider text-text-muted border-b border-app">
              <div className="col-span-3">Namn</div>
              <div className="col-span-3">Adress</div>
              <div className="col-span-2">Kund</div>
              <div className="col-span-1">Status</div>
              <div className="col-span-3 text-right">Åtgärder</div>
            </div>
            {filtered.map((o) => (
              <div
                key={o.object_id}
                data-testid={`object-row-${o.object_id}`}
                className="grid grid-cols-12 px-4 py-4 border-b border-app last:border-0 items-center"
              >
                <div className="col-span-12 md:col-span-3">
                  <div className="font-heading flex items-center gap-2 flex-wrap">
                    <span>{o.name}</span>
                    {(() => {
                      const c = fileCounts[o.object_id];
                      const total = c?.total || 0;
                      if (!total) return null;
                      const parts = [];
                      if (c.pdf) parts.push(`${c.pdf} PDF`);
                      if (c.xls) parts.push(`${c.xls} Excel`);
                      if (c.dxf) parts.push(`${c.dxf} DXF`);
                      return (
                        <span
                          data-testid={`name-files-chip-${o.object_id}`}
                          title={`${total} bifogad${total > 1 ? "e" : ""} fil${total > 1 ? "er" : ""}: ${parts.join(", ")}`}
                          className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[var(--brand-forest)] text-white border border-[var(--brand-forest)] text-sm font-semibold tabular-nums shadow-sm"
                        >
                          <Paperclip size={18} weight="fill" />
                          <span>{total} {total === 1 ? "fil" : "filer"}</span>
                          {(c.pdf > 0 || c.xls > 0 || c.dxf > 0) && (
                            <span className="flex items-center gap-1.5 pl-2 ml-0.5 border-l border-white/30 text-[12px] font-medium">
                              {c.pdf > 0 && (
                                <span
                                  data-testid={`name-files-pdf-${o.object_id}`}
                                  className="inline-flex items-center gap-0.5"
                                  title={`${c.pdf} PDF`}
                                >
                                  <FilePdf size={14} weight="fill" />{c.pdf}
                                </span>
                              )}
                              {c.xls > 0 && (
                                <span
                                  data-testid={`name-files-xls-${o.object_id}`}
                                  className="inline-flex items-center gap-0.5"
                                  title={`${c.xls} Excel`}
                                >
                                  <FileXls size={14} weight="fill" />{c.xls}
                                </span>
                              )}
                              {c.dxf > 0 && (
                                <span
                                  data-testid={`name-files-dxf-${o.object_id}`}
                                  className="inline-flex items-center gap-0.5"
                                  title={`${c.dxf} DXF`}
                                >
                                  <FileIcon size={14} weight="fill" />{c.dxf}
                                </span>
                              )}
                            </span>
                          )}
                        </span>
                      );
                    })()}
                  </div>
                  {o.description && (
                    <div className="text-xs text-text-muted mt-0.5">{o.description}</div>
                  )}
                </div>
                <div className="col-span-6 md:col-span-3 text-sm text-text-muted flex items-center gap-1">
                  {o.address ? (<><MapPin size={14} /> {o.address}</>) : "—"}
                </div>
                <div className="col-span-6 md:col-span-2 text-sm">{o.client || "—"}</div>
                <div className="col-span-6 md:col-span-1">
                  <StatusPill status={o.status} />
                </div>
                <div className="col-span-6 md:col-span-3 flex justify-end gap-2 flex-wrap">
                  {(() => {
                    const count = fileCounts[o.object_id]?.total || 0;
                    const hasFiles = count > 0;
                    return (
                      <button
                        data-testid={`files-object-${o.object_id}`}
                        onClick={() => setFilesObject(o)}
                        className={`!py-2 !px-3 inline-flex items-center gap-1.5 text-xs rounded-lg border transition-colors ${
                          hasFiles
                            ? "bg-[var(--brand-forest)]/10 border-[var(--brand-forest)]/40 text-[var(--brand-forest)] hover:bg-[var(--brand-forest)]/20"
                            : "tk-btn-outline"
                        }`}
                        title={hasFiles ? `${count} bifogad${count > 1 ? "e" : ""} fil${count > 1 ? "er" : ""}` : "Inga filer"}
                      >
                        <Paperclip size={14} weight={hasFiles ? "fill" : "regular"} />
                        Filer
                        {hasFiles && (
                          <span
                            data-testid={`files-count-${o.object_id}`}
                            className="ml-0.5 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-[var(--brand-forest)] text-white text-[10px] font-semibold tabular-nums"
                          >
                            {count}
                          </span>
                        )}
                      </button>
                    );
                  })()}
                  {isAdmin && pllEnabled && o.status !== "archived" && (
                    <button
                      data-testid={`personalliggare-object-${o.object_id}`}
                      onClick={() => setPllObject(o)}
                      className="tk-btn-outline !py-2 !px-3 inline-flex items-center gap-1.5 text-xs"
                      title="Öppna personalliggare för detta objekt"
                    >
                      <HardHat size={14} /> Personalliggare
                    </button>
                  )}
                  {isAdmin && ataEnabled && o.status !== "archived" && (
                    <button
                      data-testid={`ata-object-${o.object_id}`}
                      onClick={() => setAtaObject(o)}
                      className="tk-btn-outline !py-2 !px-3 inline-flex items-center gap-1.5 text-xs"
                      title="Öppna ÄTA-hantering för detta objekt"
                    >
                      <FileText size={14} /> ÄTA
                    </button>
                  )}
                  <button
                    data-testid={`deviations-object-${o.object_id}`}
                    onClick={() => setDeviationsObject(o)}
                    className="tk-btn-outline !py-2 !px-3 inline-flex items-center gap-1.5 text-xs border-[var(--brand-rust)]/40 text-[var(--brand-rust)] hover:bg-[var(--brand-rust)]/10"
                    title="Visa avvikelser för detta objekt"
                  >
                    <Warning size={14} weight="fill" /> Avvikelser
                  </button>
                  {isAdmin && o.client_type === "private" && (o.deduction_type === "rut" || o.deduction_type === "rot") && o.has_client_ssn && (
                    <button
                      data-testid={`rutrot-download-${o.object_id}`}
                      onClick={() => downloadRutRot(o)}
                      className="tk-btn-outline !py-2 !px-3 inline-flex items-center gap-1.5 text-xs border-[var(--brand-rust)] text-[var(--brand-rust)] hover:bg-[var(--brand-rust)]/10"
                      title={`Ladda ner ${o.deduction_type.toUpperCase()}-underlag som PDF`}
                    >
                      <FileText size={14} weight="fill" />
                      {o.deduction_type.toUpperCase()}-underlag
                    </button>
                  )}
                  {isAdmin && (o.status === "completed" || o.status === "archived") && (
                    <button
                      data-testid={`export-object-${o.object_id}`}
                      onClick={() => exportArchive(o)}
                      disabled={exportingId === o.object_id}
                      className={`!py-2 !px-3 inline-flex items-center gap-1.5 text-xs rounded-lg border transition-colors ${
                        o.status === "archived"
                          ? "tk-btn-outline"
                          : "bg-[var(--brand-rust)] text-white border-[var(--brand-rust)] hover:bg-[var(--brand-rust)]/90"
                      } ${exportingId === o.object_id ? "opacity-60 cursor-wait" : ""}`}
                      title={o.status === "archived" ? "Ladda ner arkiv igen" : "Exportera och arkivera detta objekt"}
                    >
                      <Archive size={14} weight={o.status === "archived" ? "regular" : "fill"} />
                      {exportingId === o.object_id
                        ? "Exporterar…"
                        : o.status === "archived"
                          ? "Ladda ner arkiv"
                          : "Exportera & arkivera"}
                    </button>
                  )}
                  {isAdmin && o.status === "archived" && (
                    <button
                      data-testid={`restore-object-${o.object_id}`}
                      onClick={() => restoreObject(o)}
                      className="tk-btn-outline !py-2 !px-3 inline-flex items-center gap-1.5 text-xs"
                      title="Återställ arkiverat objekt"
                    >
                      <ArrowCounterClockwise size={14} weight="bold" /> Återställ
                    </button>
                  )}
                  {isAdmin && (
                    <>
                      <button
                        data-testid={`edit-object-${o.object_id}`}
                        onClick={() => openEdit(o)}
                        className="tk-btn-outline !py-2 !px-3 inline-flex items-center gap-1 text-xs"
                      >
                        <PencilSimple size={14} /> Redigera
                      </button>
                      <button
                        data-testid={`delete-object-${o.object_id}`}
                        onClick={() => remove(o.object_id)}
                        className="text-text-muted hover:text-[var(--status-danger)] p-2"
                        title="Ta bort"
                      >
                        <Trash size={18} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
          );
        })()}
      </div>

      {open && (
        <Modal onClose={() => setOpen(false)} title={editing ? "Redigera objekt" : "Nytt objekt"} size="xl">
          <div className="space-y-4">
            <Field label="Namn *">
              <input
                data-testid="object-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="tk-input"
                placeholder="t.ex. Villa Lindberg"
              />
            </Field>
            <Field label="Adress">
              <input
                data-testid="object-address-input"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                className="tk-input"
                placeholder="Storgatan 12, Stockholm"
              />
            </Field>
            <Field label="Kund">
              <input
                data-testid="object-client-input"
                value={form.client}
                onChange={(e) => setForm({ ...form, client: e.target.value })}
                className="tk-input"
                placeholder="t.ex. Skanska Sverige AB"
              />
            </Field>

            {/* Kunduppgifter — förifylls automatiskt i ÄTA-formuläret */}
            <div className="border border-app rounded-md p-4 space-y-3 bg-[var(--surface-muted,#faf7f2)]">
              <div className="text-xs uppercase tracking-wider text-text-muted flex items-center justify-between">
                <span>Kunduppgifter (valfria)</span>
                <span className="text-[10px] normal-case tracking-normal">
                  Följer med automatiskt till ÄTA-dokument
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Org.nummer">
                  <div className="flex gap-2">
                    <input
                      data-testid="object-client-orgnr-input"
                      value={form.client_org_number}
                      onChange={(e) => setForm({ ...form, client_org_number: e.target.value })}
                      className="tk-input font-mono flex-1"
                      placeholder="556677-8899"
                    />
                    <button
                      type="button"
                      data-testid="object-orgnr-lookup-button"
                      onClick={() => handleOrgnrLookup(form.client_org_number)}
                      disabled={!form.client_org_number || orgnrLoading}
                      title="Hämta företagsuppgifter från Bolagsverket"
                      className="tk-btn-outline !py-2 !px-3 text-sm inline-flex items-center gap-1"
                    >
                      <MagnifyingGlass size={14} />
                      {orgnrLoading ? "…" : "Slå upp"}
                    </button>
                  </div>
                </Field>
                <Field label="Kontaktperson">
                  <input
                    data-testid="object-client-contact-input"
                    value={form.client_contact_name}
                    onChange={(e) => setForm({ ...form, client_contact_name: e.target.value })}
                    className="tk-input"
                    placeholder="Anna Andersson"
                  />
                </Field>
                <Field label="E-post">
                  <input
                    type="email"
                    data-testid="object-client-email-input"
                    value={form.client_email}
                    onChange={(e) => setForm({ ...form, client_email: e.target.value })}
                    className="tk-input"
                    placeholder="anna@kund.se"
                  />
                </Field>
                <Field label="Telefon">
                  <input
                    data-testid="object-client-phone-input"
                    value={form.client_phone}
                    onChange={(e) => setForm({ ...form, client_phone: e.target.value })}
                    className="tk-input font-mono"
                    placeholder="070-123 45 67"
                  />
                </Field>
              </div>
              <Field label="Avtalsreferens / Kontraktsnummer">
                <input
                  data-testid="object-contract-ref-input"
                  value={form.contract_reference}
                  onChange={(e) => setForm({ ...form, contract_reference: e.target.value })}
                  className="tk-input"
                  placeholder="Avtal 2026-04-15, Projekt-ID XYZ"
                />
              </Field>
            </div>

            {/* RUT/ROT-underlag för privatpersoner */}
            <div className="border border-app rounded-md p-4 space-y-3 bg-[var(--surface-muted,#faf7f2)]">
              <div className="text-xs uppercase tracking-wider text-text-muted flex items-center justify-between">
                <span>RUT/ROT-avdrag (privatperson)</span>
                <span className="text-[10px] normal-case tracking-normal">
                  Genererar Skatteverket-underlag som PDF
                </span>
              </div>
              <Field label="Kundtyp">
                <div className="flex gap-2">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="client_type"
                      data-testid="object-client-type-business"
                      value="business"
                      checked={form.client_type !== "private"}
                      onChange={() => setForm({ ...form, client_type: "business", deduction_type: "none" })}
                    />
                    Företag (B2B)
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="client_type"
                      data-testid="object-client-type-private"
                      value="private"
                      checked={form.client_type === "private"}
                      onChange={() => setForm({ ...form, client_type: "private" })}
                    />
                    Privatperson (kan ha RUT/ROT)
                  </label>
                </div>
              </Field>

              {form.client_type === "private" && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field label="Avdragstyp">
                      <select
                        data-testid="object-deduction-type"
                        value={form.deduction_type}
                        onChange={(e) => setForm({ ...form, deduction_type: e.target.value })}
                        className="tk-input"
                      >
                        <option value="none">Inget avdrag</option>
                        <option value="rut">RUT (städ, hemservice, trädgård)</option>
                        <option value="rot">ROT (renovering, om- & tillbyggnad)</option>
                      </select>
                    </Field>
                    <Field label={`Kundens personnummer${editing?.has_client_ssn ? " (redan sparat)" : ""}`}>
                      <input
                        type="text"
                        data-testid="object-client-ssn-input"
                        value={form.client_ssn}
                        onChange={(e) => setForm({ ...form, client_ssn: e.target.value })}
                        className="tk-input font-mono"
                        placeholder={editing?.client_ssn_masked || "ÅÅÅÅMMDD-XXXX"}
                        autoComplete="off"
                      />
                    </Field>
                    {form.deduction_type === "rot" && (
                      <Field label="Fastighetsbeteckning (ROT-krav)">
                        <input
                          data-testid="object-property-designation-input"
                          value={form.property_designation}
                          onChange={(e) => setForm({ ...form, property_designation: e.target.value })}
                          className="tk-input"
                          placeholder="Trelleborg Fjället 1:23"
                        />
                      </Field>
                    )}
                    <Field label="Timpris (kr/tim exkl moms — valfritt)">
                      <input
                        type="number"
                        step="1"
                        min="0"
                        data-testid="object-hourly-rate-input"
                        value={form.hourly_rate_sek}
                        onChange={(e) => setForm({ ...form, hourly_rate_sek: e.target.value })}
                        className="tk-input font-mono"
                        placeholder="595"
                      />
                    </Field>
                  </div>
                  <p className="text-[11px] text-text-muted leading-relaxed">
                    Personnumret krypteras vid lagring och visas bara i den nedladdade PDF:en.
                    {form.deduction_type !== "none" && (
                      <> När du har sparat objektet med personnummer syns en knapp <b>&ldquo;Ladda ner {form.deduction_type.toUpperCase()}-underlag&rdquo;</b> på objektet i listan.</>
                    )}
                  </p>
                </>
              )}
            </div>
            <Field label="Beskrivning">
              <textarea
                data-testid="object-description-input"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="tk-input"
                rows={3}
              />
            </Field>
            <Field label="Status">
              <select
                data-testid="object-status-select"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="tk-input"
              >
                <option value="active">Aktiv</option>
                <option value="paused">Pausad</option>
                <option value="completed">Slutförd</option>
              </select>
            </Field>

            <div>
              <div className="text-xs uppercase tracking-wider text-text-muted mb-2 flex items-center justify-between">
                <span>Moment som ingår *</span>
                <span className="text-[10px] normal-case tracking-normal text-text-muted">
                  Klicka <Star size={11} weight="fill" className="inline -mt-0.5 text-[var(--brand-rust)]" /> för att markera som favorit (förvald vid stämpling)
                </span>
              </div>
              {allTasks.length === 0 ? (
                <p className="text-sm text-text-muted italic py-3">
                  Inga aktiva moment finns ännu. Skapa moment via Moment-sidan först.
                </p>
              ) : (
                <div
                  data-testid="object-task-picker"
                  className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-64 overflow-auto p-2 rounded-lg border border-app bg-surface"
                >
                  {allTasks.map((t) => {
                    const included = form.task_ids.includes(t.task_id);
                    const isFav = form.favorite_task_ids.includes(t.task_id);
                    return (
                      <div
                        key={t.task_id}
                        data-testid={`task-picker-row-${t.task_id}`}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                          included
                            ? "border-[var(--brand-forest)] bg-[var(--brand-forest)]/5"
                            : "border-app hover:bg-surface-hover"
                        }`}
                      >
                        <label className="flex items-center gap-2 flex-1 cursor-pointer min-w-0">
                          <input
                            type="checkbox"
                            data-testid={`task-include-${t.task_id}`}
                            checked={included}
                            onChange={() => toggleTask(t.task_id)}
                            className="w-4 h-4 accent-[var(--brand-forest)]"
                          />
                          <span
                            className="inline-block w-3 h-3 rounded-full flex-shrink-0"
                            style={{ background: t.color || "#C25934" }}
                          />
                          <span className="text-sm truncate">{t.name}</span>
                        </label>
                        <button
                          type="button"
                          data-testid={`task-favorite-${t.task_id}`}
                          onClick={() => toggleFavorite(t.task_id)}
                          title={isFav ? "Ta bort som favorit" : "Markera som favorit"}
                          className={`p-1.5 rounded-md transition-colors ${
                            isFav
                              ? "text-[var(--brand-rust)]"
                              : "text-text-muted hover:text-[var(--brand-rust)]"
                          }`}
                        >
                          <Star size={18} weight={isFav ? "fill" : "regular"} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              <p className="text-[11px] text-text-muted mt-2">
                {form.task_ids.length} moment valda
                {form.favorite_task_ids.length > 0 && ` · ${form.favorite_task_ids.length} favorit${form.favorite_task_ids.length > 1 ? "er" : ""}`}
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <button onClick={() => setOpen(false)} className="tk-btn-outline">Avbryt</button>
            <button data-testid="save-object-button" onClick={save} className="tk-btn-primary">
              {editing ? "Spara ändringar" : "Skapa objekt"}
            </button>
          </div>
        </Modal>
      )}

      {filesObject && (
        <FilesModal
          object={filesObject}
          isAdmin={isAdmin}
          onClose={() => setFilesObject(null)}
          onFilesChanged={reloadCounts}
        />
      )}

      {pllObject && (
        <PersonalliggareModal
          object={pllObject}
          siteNumber={pllSiteNumber}
          onClose={() => setPllObject(null)}
        />
      )}

      {ataObject && (
        <AtaModal
          object={ataObject}
          onClose={() => setAtaObject(null)}
        />
      )}

      {deviationsObject && (
        <DeviationsModal
          object={deviationsObject}
          onClose={() => setDeviationsObject(null)}
        />
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-wider text-text-muted mb-2">{label}</div>
      {children}
    </label>
  );
}

function filterLabel(key) {
  if (key === "pdf") return "PDF";
  if (key === "xls") return "Excel";
  if (key === "dxf") return "DXF";
  return "";
}

function FileFilterChips({ value, onChange, fileCounts }) {
  const totals = Object.values(fileCounts).reduce(
    (acc, c) => ({
      all: acc.all + (c.total ? 1 : 0),
      pdf: acc.pdf + (c.pdf ? 1 : 0),
      xls: acc.xls + (c.xls ? 1 : 0),
      dxf: acc.dxf + (c.dxf ? 1 : 0),
    }),
    { all: 0, pdf: 0, xls: 0, dxf: 0 }
  );
  const chips = [
    { key: "all", label: "Alla" },
    { key: "pdf", label: "PDF", count: totals.pdf },
    { key: "xls", label: "Excel", count: totals.xls },
    { key: "dxf", label: "DXF", count: totals.dxf },
  ];
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="object-file-filter">
      <span className="text-xs uppercase tracking-wider text-text-muted mr-1">
        Filtrera på fil:
      </span>
      {chips.map((c) => {
        const active = value === c.key;
        const disabled = c.key !== "all" && (c.count ?? 0) === 0;
        return (
          <button
            key={c.key}
            data-testid={`file-filter-${c.key}`}
            onClick={() => onChange(c.key)}
            disabled={disabled}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors tabular-nums ${
              active
                ? "bg-[var(--brand-forest)] text-white border-[var(--brand-forest)] shadow-sm"
                : disabled
                ? "border-app text-text-muted opacity-50 cursor-not-allowed"
                : "border-app text-text-primary hover:bg-[var(--brand-forest)]/10 hover:border-[var(--brand-forest)]/40"
            }`}
          >
            {c.label}
            {c.key !== "all" && (
              <span className="ml-1.5 opacity-75">({c.count ?? 0})</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function ViewFilterTabs({ value, onChange, counts }) {
  const tabs = [
    { key: "active", label: "Aktiva", count: counts.active },
    { key: "archived", label: "Arkiverade", count: counts.archived },
    { key: "all", label: "Alla", count: counts.all },
  ];
  return (
    <div className="flex flex-wrap items-center gap-1 border-b border-app" data-testid="object-view-tabs">
      {tabs.map((t) => {
        const active = value === t.key;
        return (
          <button
            key={t.key}
            data-testid={`view-tab-${t.key}`}
            onClick={() => onChange(t.key)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors tabular-nums ${
              active
                ? "border-[var(--brand-forest)] text-[var(--brand-forest)]"
                : "border-transparent text-text-muted hover:text-text-primary"
            }`}
          >
            {t.label}
            <span className="ml-1.5 opacity-75">({t.count ?? 0})</span>
          </button>
        );
      })}
    </div>
  );
}


function StatusPill({ status }) {
  const map = {
    active: { label: "Aktiv", cls: "tk-pill-active" },
    paused: { label: "Pausad", cls: "tk-pill-warning" },
    completed: { label: "Slutförd", cls: "" },
    archived: { label: "Arkiverad", cls: "tk-pill-warning" },
  };
  const v = map[status] || map.active;
  return <span className={`tk-pill ${v.cls}`} data-testid={`status-pill-${status}`}>{v.label}</span>;
}

function Modal({ children, onClose, title, size }) {
  const maxW = size === "xl" ? "max-w-2xl" : "max-w-lg";
  return (
    <ModalPortal>
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50"
      onClick={onClose}
      data-testid="modal-backdrop"
    >
      <div
        className={`bg-surface rounded-2xl ${maxW} w-full p-6 border border-app max-h-[90vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <h2 className="font-heading text-2xl">{title}</h2>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary" data-testid="modal-close">
            <X size={20} />
          </button>
        </div>
        {children}
      </div>
    </div>
    </ModalPortal>
  );
}

// ------------------ FILES MODAL ------------------
// Inkluderar både filändelser OCH MIME-typer eftersom vissa OS
// (särskilt Windows/macOS) inte mappar .dxf till någon känd MIME och
// då blir filerna gråmarkerade i fildialogen. Att även lägga till
// "application/octet-stream" säkerställer att DXF (och andra okända
// MIMEs) alltid är valbara — extension-validering sker ändå klientside
// och serverside.
const ACCEPT = [
  ".pdf,.xls,.xlsx,.dxf,.jpg,.jpeg,.png,.heic,.heif,.webp,.gif,.mp4,.mov,.webm,.m4v,.3gp",
  "application/pdf",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "image/vnd.dxf",
  "application/dxf",
  "application/x-dxf",
  "image/x-dxf",
  "drawing/x-dxf",
  "application/acad",
  "image/*",
  "video/*",
  "application/octet-stream",
].join(",");
const ALLOWED_EXT = [
  "pdf", "xls", "xlsx", "dxf",
  "jpg", "jpeg", "png", "heic", "heif", "webp", "gif",
  "mp4", "mov", "webm", "m4v", "3gp",
];
const MAX_BYTES = 50 * 1024 * 1024;

function FilesModal({ object, isAdmin, onClose, onFilesChanged }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const r = await api.get(`/objects/${object.object_id}/files`);
      setFiles(r.data);
    } catch (e) {
      toast.error("Kunde inte hämta filer");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [object.object_id]);

  const uploadOne = async (file) => {
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) {
      toast.error(`${file.name}: endast PDF, XLS, XLSX och DXF tillåts`);
      return false;
    }
    if (file.size > MAX_BYTES) {
      toast.error(`${file.name}: större än 50 MB`);
      return false;
    }
    const fd = new FormData();
    fd.append("file", file);
    await api.post(`/objects/${object.object_id}/files`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return true;
  };

  const uploadMany = async (fileList) => {
    const list = Array.from(fileList || []);
    if (list.length === 0) return;
    setUploading(true);
    let ok = 0;
    let fail = 0;
    for (const f of list) {
      try {
        const success = await uploadOne(f);
        if (success) ok += 1;
        else fail += 1;
      } catch (err) {
        fail += 1;
        toast.error(`${f.name}: ${err.response?.data?.detail || "uppladdning misslyckades"}`);
      }
    }
    setUploading(false);
    if (ok > 0) {
      toast.success(
        ok === 1 && fail === 0
          ? "Fil uppladdad"
          : `${ok} fil${ok > 1 ? "er" : ""} uppladdad${ok > 1 ? "e" : ""}${fail ? ` · ${fail} misslyckades` : ""}`
      );
    }
    await load();
    onFilesChanged?.();
  };

  const onFileChosen = async (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    await uploadMany(list);
  };

  const onDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (uploading) return;
    await uploadMany(e.dataTransfer?.files);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragOver) setDragOver(true);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const download = async (f) => {
    try {
      const resp = await fetch(
        `${API}/objects/${object.object_id}/files/${f.file_id}/download`,
        { credentials: "include" }
      );
      if (!resp.ok) throw new Error("Nedladdning misslyckades");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = f.original_name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error("Kunde inte ladda ner filen");
    }
  };

  const remove = async (f) => {
    if (!window.confirm(`Ta bort "${f.original_name}"?`)) return;
    try {
      await api.delete(`/objects/${object.object_id}/files/${f.file_id}`);
      toast.success("Borttagen");
      await load();
      onFilesChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fel vid borttagning");
    }
  };

  return (
    <Modal onClose={onClose} title={`Filer — ${object.name}`}>
      <div
        className="space-y-4"
        data-testid="files-modal"
        onDragOver={onDragOver}
        onDragEnter={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-xs text-text-muted">
            PDF, XLS, XLSX, DXF, bilder (JPG/PNG/HEIC) & video (MP4/MOV) · max 50 MB · flera filer tillåts
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <label
              className={`tk-btn-outline inline-flex items-center gap-2 cursor-pointer md:hidden ${uploading ? "opacity-60 pointer-events-none" : ""}`}
              data-testid="capture-photo-label"
              title="Öppna kameran direkt (mobil)"
            >
              📷 Foto/film
              <input
                data-testid="capture-photo-input"
                type="file"
                accept="image/*,video/*"
                capture="environment"
                className="hidden"
                onChange={onFileChosen}
                disabled={uploading}
              />
            </label>
            <label
              className={`tk-btn-primary inline-flex items-center gap-2 cursor-pointer ${uploading ? "opacity-60 pointer-events-none" : ""}`}
              data-testid="upload-file-label"
            >
              <UploadSimple size={16} weight="bold" />
              {uploading ? "Laddar upp…" : "Ladda upp fil"}
              <input
                data-testid="upload-file-input"
                type="file"
                accept={ACCEPT}
                multiple
                className="hidden"
                onChange={onFileChosen}
                disabled={uploading}
              />
            </label>
          </div>
        </div>

        <div
          data-testid="files-dropzone"
          className={`relative border-2 border-dashed rounded-xl transition-colors ${
            dragOver
              ? "border-[var(--brand-forest)] bg-[var(--brand-forest)]/10"
              : "border-app"
          }`}
        >
          {dragOver && (
            <div
              data-testid="dropzone-overlay"
              className="absolute inset-0 flex items-center justify-center rounded-xl bg-[var(--brand-forest)]/15 pointer-events-none z-10"
            >
              <span className="text-sm font-medium text-[var(--brand-forest)] flex items-center gap-2">
                <UploadSimple size={18} weight="bold" />
                Släpp filerna här — laddas upp till {object.name}
              </span>
            </div>
          )}
          <div className="divide-y divide-app max-h-80 overflow-auto">
            {loading ? (
              <p className="p-6 text-sm text-text-muted text-center">Laddar…</p>
            ) : files.length === 0 ? (
              <p className="p-8 text-sm text-text-muted text-center" data-testid="files-empty">
                Inga filer ännu. Dra och släpp filer hit eller klicka på "Ladda upp fil".
              </p>
            ) : (
            files.map((f) => (
              <div
                key={f.file_id}
                data-testid={`file-row-${f.file_id}`}
                className="flex items-center gap-3 p-3"
              >
                <FileTypeIcon name={f.original_name} />
                <div className="flex-1 min-w-0">
                  <button
                    onClick={() => setPreviewFile(f)}
                    className="block w-full text-left text-sm hover:underline truncate"
                    title={`Förhandsvisa ${f.original_name}`}
                    data-testid={`preview-file-${f.file_id}`}
                  >
                    {f.original_name}
                  </button>
                  {f.updated_by_integration && f.updated_at && (
                    <div
                      className="text-[11px] text-text-muted mt-0.5 truncate"
                      data-testid={`file-integration-stamp-${f.file_id}`}
                      title={`Senast ersatt via ${f.updated_by_integration}`}
                    >
                      Uppdaterad: {formatYmd(f.updated_at)} via {f.updated_by_integration}
                    </div>
                  )}
                </div>
                <span className="text-xs text-text-muted whitespace-nowrap">
                  {formatBytes(f.size)}
                </span>
                <button
                  onClick={() => setPreviewFile(f)}
                  className="tk-btn-outline !py-1.5 !px-2 inline-flex items-center gap-1 text-xs"
                  title="Förhandsvisa"
                  data-testid={`preview-btn-${f.file_id}`}
                >
                  <Eye size={14} />
                </button>
                <button
                  onClick={() => download(f)}
                  className="tk-btn-outline !py-1.5 !px-2 inline-flex items-center gap-1 text-xs"
                  title="Ladda ner"
                  data-testid={`download-btn-${f.file_id}`}
                >
                  <DownloadSimple size={14} />
                </button>
                {isAdmin && (
                  <button
                    onClick={() => remove(f)}
                    className="text-text-muted hover:text-[var(--status-danger)] p-1.5"
                    title="Ta bort"
                    data-testid={`delete-file-${f.file_id}`}
                  >
                    <Trash size={16} />
                  </button>
                )}
              </div>
            ))
          )}
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3 mt-6">
        <button onClick={onClose} className="tk-btn-outline" data-testid="close-files-modal">
          Stäng
        </button>
      </div>

      {previewFile && (
        <FilePreviewModal
          object={object}
          file={previewFile}
          onClose={() => setPreviewFile(null)}
          onDownload={download}
        />
      )}
    </Modal>
  );
}

function FileTypeIcon({ name }) {
  const ext = (name.split(".").pop() || "").toLowerCase();
  if (ext === "pdf") return <FilePdf size={22} className="text-[var(--brand-rust)]" />;
  if (ext === "xls" || ext === "xlsx") return <FileXls size={22} className="text-[var(--brand-forest)]" />;
  if (["jpg", "jpeg", "png", "heic", "heif", "webp", "gif"].includes(ext))
    return <FileImage size={22} className="text-blue-600" />;
  if (["mp4", "mov", "webm", "m4v", "3gp"].includes(ext))
    return <FileVideo size={22} className="text-purple-600" />;
  return <FileIcon size={22} className="text-text-muted" />;
}

function formatBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} kB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatYmd(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  } catch {
    return String(iso).slice(0, 10);
  }
}

// ------------------ DEVIATIONS MODAL ------------------
function DeviationsModal({ object, onClose }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [fileCounts, setFileCounts] = useState({});
  const [mediaFor, setMediaFor] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const r = await api.get(`/objects/${object.object_id}/deviations`);
      const list = r.data?.deviations || [];
      setRows(list);
      // Hämta filantal per rapport
      try {
        const cr = await api.get(`/time-reports/deviation-file-counts`);
        setFileCounts(cr.data || {});
      } catch {
        setFileCounts({});
      }
    } catch (_e) {
      toast.error("Kunde inte hämta avvikelser");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [object.object_id]);

  const downloadPdf = async () => {
    try {
      setDownloading(true);
      const resp = await fetch(
        `${API}/objects/${object.object_id}/deviations/pdf`,
        { credentials: "include" }
      );
      if (!resp.ok) throw new Error("Nedladdning misslyckades");
      const blob = await resp.blob();
      const safe = (object.name || "objekt")
        .replace(/[^\w\-]+/g, "_")
        .replace(/^_+|_+$/g, "") || "objekt";
      const ts = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `avvikelser-${safe}-${ts}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Avvikelselista nedladdad");
    } catch (_e) {
      toast.error("Kunde inte ladda ner PDF");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Modal onClose={onClose} title={`Avvikelser — ${object.name}`} size="xl">
      <div data-testid="deviations-modal" className="space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-xs text-text-muted">
            Fritext-avvikelser registrerade på moment för detta objekt.
          </p>
          <button
            data-testid="download-deviations-pdf"
            onClick={downloadPdf}
            disabled={downloading || loading || rows.length === 0}
            className="tk-btn-primary inline-flex items-center gap-2 disabled:opacity-60"
            title={rows.length === 0 ? "Inga avvikelser att skriva ut" : "Ladda ner som PDF"}
          >
            <DownloadSimple size={16} weight="bold" />
            {downloading ? "Genererar…" : "Ladda ner PDF"}
          </button>
        </div>

        {loading ? (
          <p className="p-6 text-sm text-text-muted text-center">Laddar…</p>
        ) : rows.length === 0 ? (
          <p
            data-testid="deviations-empty"
            className="p-8 text-sm text-text-muted text-center border border-dashed border-app rounded-xl"
          >
            Inga avvikelser registrerade på detta objekt ännu.
            <br />
            <span className="text-[11px]">
              Anställda kan lägga till avvikelse när de stämplar ut eller byter moment.
            </span>
          </p>
        ) : (
          <div className="divide-y divide-app border border-app rounded-xl max-h-[60vh] overflow-auto">
            {rows.map((d) => (
              <div
                key={d.report_id}
                data-testid={`deviation-row-${d.report_id}`}
                className="p-4 space-y-1.5"
              >
                <div className="flex items-center gap-2 flex-wrap text-[11px] uppercase tracking-wider text-text-muted">
                  <span className="tk-pill tk-pill-warning !text-[10px] !py-0.5">
                    {d.task_name || "moment"}
                  </span>
                  <span>{formatYmd(d.start_time)}</span>
                  <span>·</span>
                  <span>{d.user_name || "—"}</span>
                  <div className="ml-auto">
                    <button
                      data-testid={`deviation-media-btn-${d.report_id}`}
                      onClick={() => setMediaFor(d)}
                      className="text-[11px] normal-case tracking-normal text-brand hover:underline inline-flex items-center gap-1"
                      title="Foto & film"
                    >
                      <Paperclip size={12} />
                      Bilagor{fileCounts[d.report_id] > 0 ? ` (${fileCounts[d.report_id]})` : ""}
                    </button>
                  </div>
                </div>
                <p className="text-sm text-text-strong whitespace-pre-wrap">
                  {d.deviation}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-end gap-3 mt-6">
        <button onClick={onClose} className="tk-btn-outline" data-testid="close-deviations-modal">
          Stäng
        </button>
      </div>
      {mediaFor && (
        <MediaGalleryModal
          basePath={`/time-reports/${mediaFor.report_id}`}
          title={`Bilagor — ${mediaFor.task_name || "avvikelse"} (${formatYmd(mediaFor.start_time)})`}
          onClose={() => setMediaFor(null)}
          onChanged={() => load()}
        />
      )}
    </Modal>
  );
}

